import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import StatusBanner from './StatusBanner';
import { useStatus } from '../context/StatusContext';

interface NavLinkDef {
  name: string;
  path: string;
}

/** Primary destinations. The logo also returns home. */
const navLinks: NavLinkDef[] = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Our Work', path: '/work' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const focusRing =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { publicHubStatusEnabled, statusError, loading: statusLoading, retryStatus } = useStatus();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Mobile menu: focus trap + Escape.
  useEffect(() => {
    if (!isMenuOpen) return;
    const menu = mobileMenuRef.current;
    if (!menu) return;
    const focusable = menu.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        toggleButtonRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || focusable.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 w-screen z-50 flex flex-col pt-[env(safe-area-inset-top)] transition-colors duration-300 ${
        isScrolled
          ? 'bg-secondary-900/95 backdrop-blur-md shadow-xl border-b border-secondary-700/30'
          : 'bg-secondary-900/85 backdrop-blur-sm'
      }`}
    >
      <nav className="flex-shrink-0" aria-label="Primary">
        <div className="page-inner h-14 flex items-center justify-between gap-3">
          <Link
            to="/"
            className={`flex items-center gap-2.5 group min-w-0 rounded-lg ${focusRing}`}
            aria-label="FairCorp Hub — home"
          >
            <img src="/FinalLogoRebuild.png" alt="" className="w-9 h-9 flex-shrink-0 object-contain group-hover:scale-105 transition-transform duration-300" />
            <span className="font-semibold tracking-tight text-white group-hover:text-primary-300 transition-colors truncate">
              FairCorp Hub
            </span>
          </Link>

          {/* Tablet / desktop */}
          <ul className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                  className={`px-3.5 py-2 min-h-[44px] inline-flex items-center rounded-lg text-sm font-medium transition-colors border ${focusRing} ${
                    isActive(link.path)
                      ? 'text-primary-300 bg-primary-500/15 border-primary-400/35'
                      : 'text-secondary-300 hover:text-white hover:bg-secondary-800/50 border-transparent'
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Mobile toggle */}
          <button
            ref={toggleButtonRef}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden relative min-w-[44px] min-h-[44px] flex items-center justify-center text-secondary-300 hover:text-primary-300 transition-colors rounded-lg ${focusRing}`}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav-menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center" aria-hidden="true">
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`} />
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`} />
            </div>
          </button>
        </div>

        <StatusBanner />

        {publicHubStatusEnabled && statusError && !statusLoading && (
          <div
            className="w-full px-4 py-2 text-center text-sm bg-error-950/80 border-b border-error-800/50 text-error-200 flex flex-wrap items-center justify-center gap-2"
            role="alert"
          >
            <span className="line-clamp-2">Status could not be refreshed: {statusError}</span>
            <button
              type="button"
              onClick={retryStatus}
              className="shrink-0 min-h-[44px] px-3 py-1.5 rounded-md border border-error-500/50 hover:bg-error-900/50 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              Retry
            </button>
          </div>
        )}

        <div
          id="mobile-nav-menu"
          ref={mobileMenuRef}
          hidden={!isMenuOpen}
          className="md:hidden border-t border-secondary-800/55 bg-secondary-950/95 backdrop-blur-md max-h-[calc(100dvh-var(--header-height,3.5rem))] overflow-y-auto"
        >
          <ul className="page-inner py-2">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                  className={`flex items-center min-h-[48px] px-4 rounded-lg font-medium ${focusRing} ${
                    isActive(link.path)
                      ? 'text-primary-300 bg-primary-500/15 border border-primary-500/30'
                      : 'text-secondary-200 hover:text-white hover:bg-secondary-800/50 border border-transparent'
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
